import React, { forwardRef, useMemo } from "react";
import clsx from "clsx";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import letterTemplateImage from "@/assets/north-pole-elf-letter-template.png";

interface LetterPreviewProps {
  content?: string;
  childName?: string;
  isLoading?: boolean;
}

const LetterPreview = forwardRef<HTMLDivElement, LetterPreviewProps>(({
  content,
  childName,
  isLoading,
}, ref) => {
  const formattedContent = useMemo(() => {
    if (!content) return null;

    const sanitized = content.replace(/\r\n/g, "\n").trim();
    if (!sanitized) return null;

    const normalized = sanitized.replace(/\n{3,}/g, "\n\n");
    const paragraphs = normalized.split(/\n{2,}/);

    return paragraphs.map((paragraph, paragraphIndex) => {
      const lines = paragraph.split("\n").map((line) => line.trim());
      return (
        <p
          key={`paragraph-${paragraphIndex}`}
          className="elf-letter-paragraph"
        >
          {lines.map((line, lineIndex) => (
            <span key={`line-${paragraphIndex}-${lineIndex}`}>
              {line}
              {lineIndex < lines.length - 1 && <br />}
            </span>
          ))}
        </p>
      );
    });
  }, [content]);

  return (
    <div className="flex flex-col">
      <div className="mb-3 md:mb-4">
        <h3 className="text-base md:text-lg font-semibold text-foreground">Letter Preview</h3>
        <p className="text-xs md:text-sm text-muted-foreground">
          {content
            ? `Previewing your letter${childName ? ` for ${childName}` : ""}. Enter your email below to receive the printable PDF.`
            : "Your generated letters will appear here."}
        </p>
      </div>

      <p className="elf-letter-print-notice">
        To print your elf letter, enter your email at elfletterbot.com/elf-letter-generator
        and open the PDF attachment in your inbox.
      </p>
      <div className="elf-letter-screen-preview w-full flex items-center justify-center">
        <div className="elf-letter-container w-full">
          <AspectRatio
            ratio={8.5 / 11}
            className="bg-white"
          >
            <div
              ref={ref}
              className={clsx(
                "elf-letter-template",
                isLoading && "animate-pulse opacity-75 saturate-75",
              )}
            >
              <div className="elf-letter-background" aria-hidden="true">
                <img
                  src={letterTemplateImage}
                  alt="North Pole letter template"
                  className="elf-letter-background-image"
                  loading="eager"
                />
              </div>
              <div className="elf-letter-content-area">
                <div className="elf-letter-text">
                  {formattedContent ? (
                    formattedContent
                  ) : (
                    <div className="text-muted-foreground">
                      <p className="elf-letter-paragraph font-semibold text-neutral-800">
                        Your elf letter will show up here.
                      </p>
                      <p className="elf-letter-paragraph">
                        Fill in your child&apos;s name and a few magical details,
                        then tap&nbsp;
                        <span className="font-medium text-elfRed-ink">
                          Generate Letter
                        </span>
                        .
                      </p>
                      <p className="elf-letter-paragraph">
                        Love the magic? Enter your email to receive a printable
                        PDF of your letter.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </AspectRatio>
        </div>
      </div>
    </div>
  );
});

LetterPreview.displayName = "LetterPreview";

export default LetterPreview;
